import { IRemoteAgentSiteSettings, ISiteAdminBridgeSettings } from './ISiteAdminBridgeSettings';
import { ISiteCommentingSettings } from './ISiteCommentingSettings';

// a generic interface that allows us to pass bridge settings, extension settings, etc to the coordinator ctrl
type SiteAdminSettingsCombined =
  | ISiteAdminSettings
  | IDashExtSiteSettings
  | ISiteAdminBridgeSettings
  | IMetadataDrivenSiteSettings;

export interface ISiteSettingsForSiteAdmin {
  siteId: string;
  siteAdminSettings: SiteAdminSettingsCombined;
  subscriptionSettings?: ISiteSubscriptionSettings;
}

export interface ISiteAdminSettings {
  addSupportUserEnabled: boolean;
  attributeCaptureEnabled?: boolean;
  alternativeFederationEngineEnabled: boolean;
  derivedPermissionsEnabled: boolean;
  notificationEnabled: boolean;
  refreshTokenEnabled: boolean;
  remoteAgentSettings?: IRemoteAgentSiteSettings;
  remoteQueryingEnabled: boolean;
  sheetImageEnabled: boolean;
  siteInviteEmailEnabled: boolean;
  generativeAiEnabled: boolean;
  pulsePersonalizedRankingEnabled: boolean;
  extractEncryptionMode: string;
  cmekAvailable: boolean;
  isAdminDataSourceEnabled: boolean;
  siteStartPageUri: string;
  siteNamedSharingSettings: ISiteNamedSharingSettings;
  vizRecommendationsEnabled: boolean;
  vizRecUsernameVisibilityEnabled: boolean;
  sitePersonalSpaceSettings: ISitePersonalSpaceSettings;
  explainDataEnabled: boolean;
  dataOrientationEnabled: boolean;
  workflowExtensionEnabled: boolean;
  userVisibility: UserVisibilityMode;
  requestAccessEnabled: boolean;
  siteCommentingSettings?: ISiteCommentingSettings;
  dataAlertsEnabled: boolean;
  webZoneContentEnabled: boolean;
  notifySiteAdminsOnThrottleEnabled: boolean;
  runNowEnabled: boolean;
  flowFederationStrategySelectedConnectionEnabled: boolean;
  catalogingEnabled: boolean;
  catalogObfuscationEnabled: boolean;
  timeZone: string;
  tagLimit: number;
  useDefaultTimeZone: boolean;
  publicCollectionsEnabled?: boolean;
  mobileBiometricsEnabled?: boolean;
  einsteinInFlowEnabled: boolean;
  canOwnerAccelerate?: boolean;
  linkedTaskSchedulingEnabled?: boolean;
  linkedTaskRunNowEnabled?: boolean;
  dqwSubscriptionsEnabled?: boolean;
  workbookAccelerationSettings?: IWorkbookAccelerationSettings;
  flowOutputSubscriptionSettings?: IFlowOutputSubscriptionSettings;
  flowParametersEnabled?: boolean;
  flowParametersAnyTypeEnabled?: boolean;
  flowParametersSystemTypeEnabled?: boolean;
  adminInsightsPublishFrequency: number;
  publishToSalesforceEnabled: boolean;
  collectionsEnabled: boolean;
  subscriptionAndDDAEmailLinksEnabled: boolean;
  allowPatCreation?: boolean;
  allowPatGroup?: string;
  patAbsoluteExpiry?: number;
  autoFlowRunMonitoringEnabled: boolean;
  autoExtractRefreshMonitoringEnabled: boolean;
  groupAssertionsEnabled: boolean;
  emailCustomizationSettings: ISiteEmailCustomizationSettings;
  pulseEnabled?: boolean;
  pulseGroupId?: string;
  groupAssertionsSAMLEnabled: boolean;
  groupAssertionsOIDCEnabled: boolean;
  groupAssertionsConnectedAppsEnabled: boolean;
  generativeAiPulse: boolean;
  generativeAiPulseDiscoverEnabled: boolean;
  generativeAiPulseExternalEmbeddingsEnabled: boolean;
  generativeAiPrep: boolean;
  generativeAiDataCatalog: boolean;
  generativeAiWebAuthoring: boolean;
  generativeAiPulseDiscoverGroupId?: string;
  generativeAiPulseExternalEmbeddingsGroupId?: string;
  generativeAiPrepGroupId?: string;
  generativeAiDataCatalogGroupId?: string;
  generativeAiWebAuthoringGroupId?: string;
  generativeAiGroupId?: string;
  dashboardInsightsEnabled: boolean;
  dashboardInsightsGroupId?: string;
  generativeAiWebAuthoringOnPremEnabled: boolean;
  dashboardNarrativeEnabled: boolean;
  generativeAiLLMProviderAPIKey: string;
  groupSetsEnabled: boolean;
  blockNonExtractEnabled: boolean;
  recycleBinSettings: ISiteRecycleBinSettings;
  siteSettingsV2?: ISiteSettingsV2;
  exploreInTableauEnabled: boolean;
}

// Minimal site-admin-settings shape for Admin Profile users, who cannot call
// the legacy /sites endpoint that populates ISiteAdminSettings. Their form is
// metadata-driven and only reads/writes siteSettingsV2.
export interface IMetadataDrivenSiteSettings {
  siteSettingsV2: ISiteSettingsV2;
}

// The getSiteSettingsForSiteAdmin returns the data as ISiteSettingsForSiteAdmin,
// but subscriptionSettings should really be inside siteAdminSettings.
// This is an adapter interface to make that happen until the API itself is updated.
export interface ISiteAdminSettingsWithSubscriptions extends ISiteAdminSettings {
  subscriptionSettings: ISiteSubscriptionSettings;
}

export interface IWorkbookAccelerationSettings {
  viewsLimitPerSiteForAccelerationEnabled: boolean;
  viewsLimitPerSiteForAcceleration: number;
  autoSuspendSettings: IAutoSuspendSettings;
}

export interface IAutoSuspendSettings {
  autoSuspendFailureLimitForAcceleration: number;
  autoSuspendFailureLimitForAccelerationEnabled: boolean;
  autoSuspendFailureLimitForAccelerationTimePeriod: string;
  autoSuspendAccelerationImprovementThresholdEnabled: boolean;
  autoSuspendAccelerationImprovementThreshold: number;
  autoSuspendViewsLimit: number;
  autoSuspendViewsLimitEnabled: boolean;
  autoSuspendViewsLimitTimePeriod: string;
}

export interface IFlowOutputSubscriptionSettings {
  flowOutputSubscriptionsEnabled?: boolean;
  flowOutputSubscriptionsDataInEmailBodyEnabled?: boolean;
  flowOutputSubscriptionsDataAsEmailAttachmentEnabled?: boolean;
}

export interface ISiteNamedSharingSettings {
  namedSharingEnabled: boolean;
}

export interface ISitePersonalSpaceSettings {
  personalSpaceEnabled: boolean;
  personalSpaceStorageQuotaSet: boolean;
  personalSpaceStorageQuota: number;
}

export enum SMTPAuthType {
  BASIC='BASIC',
  MS_OAUTH='MS_OAUTH'
}

export interface ISiteEmailCustomizationSettings {
  /** Whether email customization for DDA and Subscription emails is on */
  isEmailCustomizationEnabled: boolean;
  /** The customer specified domain that is used in email hyperlinks and from address */
  emailCustomizationDomain: string;
  /** Port for SMTP server */
  emailCustomizationSMTPSPort: number;
  /** Local name for SMTP server */
  emailCustomizationSenderLocalNameOrEmail: string;
  /** URL for SMTP server */
  emailCustomizationSMTPSUrl: string;
  /** Friendly name for sender from address */
  emailCustomizationSenderFriendlyName: string;
  /** Which auth type is being used  */
  emailCustomizationSMTPAuthType: SMTPAuthType;
  /** MS OAuth Client ID */
  emailCustomizationClientId: string;
  /** MS OAuth Tenant ID */
  emailCustomizationTenantId: string;
}

export interface ISiteRecycleBinSettings {
  /** Whether Recycle Bin is on */
  isRecycleBinEnabled: boolean;
  /** Lifespan for recycled content */
  recycleBinContentLifespan: number;
}

export type ISiteSettingsV2 = Record<string, any>;

export interface ExtensionSafeListEntry {
  url: string;
  fullDataAllowed: boolean;
  promptNeeded: boolean;
}

export interface ISiteDashExtSettingsForSiteAdmin {
  siteId: string;
  featureSettings: IDashExtSiteSettings;
}

export interface IDashExtSiteSettings {
  extensionsEnabled: boolean;
  allowSandboxed?: boolean;          // Only use if vizExtensionsTrustedTableau and vizExtensionsTrustedPartner FeatureFlags are both false
  allowTrusted?: boolean;            // Only use if either vizExtensionsTrustedTableau or vizExtensionsTrustedPartner FeatureFlags are true
  includeSandboxed?: boolean;        // Only use if either vizExtensionsTrustedTableau or vizExtensionsTrustedPartner FeatureFlags are true
  includeTableauBuilt?: boolean;     // Only use if vizExtensionsTrustedTableau FeatureFlag is true
  includePartnerBuilt?: boolean;     // Only use if vizExtensionsTrustedPartner FeatureFlag is true 
  safeList: ExtensionSafeListEntry[];
}

export interface IPrepExtensionsSiteSettings {
  einsteinEnabled: boolean;
}

export interface IPrepExtensionsServerSettings {
  einsteinEnabled: boolean;
}

export interface IAnalyticsExtSiteSettings {
  enabled: boolean;
}

export interface IAnalyticsExtConnectionId {
  connection_luid: string;
}

export enum AnalyticsExtConnectionType {
  TABPY,
  RSERVE,
  EINSTEIN_DISCOVERY,
  GENERIC_API
}

export interface IAnalyticsExtConnectionSettings {
  host: string;
  port: number;
  is_auth_enabled: boolean;
  username: string;
  password: string;
  is_ssl_enabled: boolean;
  connection_brief: IAnalyticsConnectionBrief;
  connection_luid: string;
}

export interface IAnalyticsConnectionBrief {
  connection_name: string;
  connection_type: string;
}

export interface IAnalyticsConnectionMetadata {
  connection_luid: string;
  connection_brief: IAnalyticsConnectionBrief;
}

export interface IAnalyticsConnectionMapping {
  workbook_luid: string;
  connection_luid: string;
}

export interface IAnalyticsExtConnectionId {
  connection_luid: string;
}

export interface IAnalyticsExtWorkbookId {
  workbook_luid: string;
}

export enum UserVisibilityMode {
  FULL = 'FULL',
  LIMITED = 'LIMITED',
}

export enum UserQuotaMode {
  SERVER_LIMIT = 'server-limit',
  SITE_USER_QUOTA = 'site-user-quota',
  SITE_TIERED_CAPACITY = 'site-tiered-capacity'
}

export interface ISiteSubscriptionSettings {
  subscriptionsEnabled: boolean;
  subscribeOthersEnabled: boolean;
  customScheduleSubscriptionForSiteEnabled: boolean;
  allowSubscriptionAttachments: boolean;
  serverAllowSubscriptionAttachments: boolean;
}

export interface IPulseSiteSettingsV2 {
  /** Pulse digest start time in minutes since midnight */
  pulseDigestStartTime?: number;
  /** Whether email notifications are enabled for Pulse (default: true) */
  pulseEmailNotificationsEnabled?: boolean;
  /** Whether Slack notifications are enabled for Pulse (default: true) */
  pulseSlackNotificationsEnabled?: boolean;
  /** Whether MS Teams notifications are enabled for Pulse (default: false) */
  pulseMSTeamsNotificationsEnabled?: boolean;
  /** Day of week for weekly digest (0=Monday, 1=Tuesday, ..., 6=Sunday) */
  pulseWeeklyDigestDay?: number;
  /** Monthly digest mode (0=Day mode, 1=First week, 2=Second week, ..., 5=Fifth week, 6=Last week) */
  pulseMonthlyDigestDayOrWeek?: number;
  /** Day of month (1-31) in Day mode, or day of week (0-6) in weekday mode */
  pulseMonthlyDigestDateOrDay?: number;
}
